import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { AuthService } from '../services/auth.service';

/**
 * Turns a 401 on an authenticated staff request into a re-login prompt instead
 * of a silent failure. The JWT is 24h with no refresh, so mid-session expiry
 * otherwise leaves the UI looking logged-in while every call quietly fails.
 *
 * Scope is deliberately narrow:
 *  - only /api requests,
 *  - NOT the auth endpoints whose 401 is a normal local outcome: login/signup
 *    (bad credentials, shown in the dialog) and logout (already tearing down).
 */
@Injectable()
export class UnauthorizedInterceptor implements HttpInterceptor {
  constructor(private auth: AuthService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    return next.handle(req).pipe(
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401 && this.shouldHandle(req.url)
            && req.headers.get('Authorization') === `Bearer ${this.auth.getToken()}`) {
          this.auth.handleSessionExpired();
        }
        return throwError(() => err);
      }),
    );
  }

  private shouldHandle(url: string): boolean {
    if (!url.startsWith('/api')) return false;
    return !/\/api\/auth\/(login|signup|logout)\b/.test(url);
  }
}
